import React, { useState, useEffect } from "react";
import { useAsyncError, useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Box,
  Typography,
  Modal,
  Icon,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { API_URL } from "../api";
import { CloudUpload, Description, Close } from "@mui/icons-material";
import "../css/FormAbsensi.css";
import { CustomModal } from "../components/CustomModal";

export default function FormLaporanHarian() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    lokasi: "",
    keterangan: "",
    pdf: null,
    nama: "",
  });
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "info",
    buttons: [],
  });

  const showModal = (title, message, type = "info", buttons = []) => {
    setModalConfig({ title, message, type, buttons });
    setModalVisible(true);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);

        setFormData((prev) => ({ ...prev, nama: userData.nama || "" }));
      } catch (error) {
        console.error("Gagal mem-parsing data pengguna:", error);
      }
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setFormData((prev) => ({ ...prev, pdf: file }));
      if (errors.pdf) setErrors((prev) => ({ ...prev, pdf: null }));
    } else {
      setErrors((prev) => ({
        ...prev,
        pdf: "Hanya file PDF yang diperbolehkan",
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.lokasi) newErrors.lokasi = "Lokasi";
    if (!formData.nama) newErrors.nama = "Nama";
    if (!formData.pdf) newErrors.pdf = "File PDF";

    if (Object.keys(newErrors).length > 0) {
      const errorFields = Object.values(newErrors).join(", ");
      showModal(
        "Data Belum Lengkap",
        `Harap lengkapi data ${errorFields} `,
        "warning"
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsUploading(true);
    try {
      const token = localStorage.getItem("userToken");
      const formDataToSend = new FormData();
      formDataToSend.append("lokasi", formData.lokasi);
      formDataToSend.append("keterangan", formData.keterangan);
      formDataToSend.append("nama", formData.nama);
      formDataToSend.append("pdf", formData.pdf);

      await axios.post(`${API_URL}/laporan-harian/upload`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      showModal("Berhasil", "Laporan harian berhasil diupload.", "success", [
        { text: "OK", onPress: () => window.history.back() },
      ]);
    } catch (error) {
      console.error("Upload error:", error);
      let message = "Gagal mengunggah laporan.";
      if (error.response?.status === 401) {
        message = "Sesi habis, silakan login kembali.";
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }
      showModal("Gagal", message, "error");
    } finally {
      setIsUploading(false);
    }
  };
  const handleRemoveFile = () => {
    setFormData((prev) => ({ ...prev, pdf: null }));
  };

  return (
    <div className="form-container">
      <header className="app-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate("/menu")}>
            <Icon>arrow_back</Icon>
          </button>
          <h1 className="header-title"> Laporan Harian</h1>
          <div className="header-spacer"></div>
        </div>
      </header>

      <Box sx={{ mx: "auto", mt: 4, p: 2 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            select
            label="Tim"
            name="lokasi"
            fullWidth
            margin="normal"
            value={formData.lokasi}
            onChange={handleChange}
            error={!!errors.lokasi}
            helperText={errors.lokasi}
          >
            {[
              { label: "Pit Utara", value: "Pit Utara, KM 22" },
              { label: "Pit Selatan", value: "Pit Selatan, Office 33" },
              { label: "Drone", value: "Drone, Head Office" },
            ].map((loc) => (
              <MenuItem key={loc.value} value={loc.value}>
                {loc.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Nama"
            fullWidth
            margin="normal"
            name="nama"
            value={formData.nama}
            InputProps={{ readOnly: true }}
            error={!!errors.nama}
            helperText={errors.nama}
          />

          <TextField
            label="Keterangan (Opsional)"
            fullWidth
            margin="normal"
            name="keterangan"
            multiline
            rows={3}
            value={formData.keterangan}
            onChange={handleChange}
          />

          {formData.pdf ? (
            // Tampilan setelah file dipilih
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                my: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "grey.100",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Description sx={{ mr: 1.5, color: "primary.main" }} />
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {formData.pdf.name}
                </Typography>
              </Box>
              <IconButton onClick={handleRemoveFile} size="small">
                <Close />
              </IconButton>
            </Paper>
          ) : (
            // Tampilan area upload
            <Box
              component="label"
              sx={{
                border: "2px dashed",
                borderColor: "grey.400",
                borderRadius: 1,
                p: 4,
                my: 2,
                textAlign: "center",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "grey.600",
                transition: "border-color 0.3s, color 0.3s",
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              <input
                hidden
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
              />
              <CloudUpload sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6">
                Klik untuk mengunggah atau seret file PDF ke sini
              </Typography>
            </Box>
          )}
          {errors.pdf && <Typography color="error">{errors.pdf}</Typography>}
          {/* Modal */}
          <CustomModal
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            title={modalConfig.title}
            message={modalConfig.message}
            type={modalConfig.type}
            buttons={modalConfig.buttons}
          />
        </Paper>

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={isUploading}
        >
          {isUploading ? <CircularProgress size={24} /> : "Kirim Laporan"}
        </Button>
      </Box>
    </div>
  );
}
